import "./currencies-page.css";
import PageTitle from "../../layouts/PageTitle";
import { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getCurrenciesFunc } from "../../../store/custom-actions/currencies-actions/GetCurrenciesSlicer";
import { deleteCurrencyFunc } from "../../../store/custom-actions/currencies-actions/DeleteCurrencySlicer";
import { setDefaultCurrencyFunc } from "../../../store/custom-actions/currencies-actions/SetDefaultCurrencySlicer";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";
import { useTranslation } from "react-i18next";
import Pagination from "../../components/ui/Pagination";
import Table from "../../components/ui/Table";
import SearchBox from "../../components/ui/SearchBox";
import { handleDeleteEntity } from "../../../utils/deleteHelper";
import checkPermissions from "../../global-functions/checkPermissionsFunc";
import { toast } from "react-toastify";
import NewCurrencyForm from "../../custom-forms/currencies/NewCurrencyForm";
import UpdateCurrencyForm from "../../custom-forms/currencies/UpdateCurrencyForm";

function CurrenciesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [showNewCurrencyForm, setShowNewCurrencyForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [currencyToUpdate, setCurrencyToUpdate] = useState({});
  
  const { loading: getCurrenciesLoading, data: currenciesData } = useSelector(
    (state) => state.getCurrenciesSlicer
  );
  const { loading: deleteCurrencyLoading } = useSelector(
    (state) => state.deleteCurrencySlicer
  );
  const { loading: setDefaultLoading } = useSelector(
    (state) => state.setDefaultCurrencySlicer
  );

  useEffect(() => {
    dispatch(getCurrenciesFunc());
  }, [dispatch]);

  // Get current page data directly from currenciesData
  const getCurrentPageData = () => {
    if (!currenciesData || currenciesData.length === 0) return [];
    return currenciesData.slice(
      activePag.current * sort,
      (activePag.current + 1) * sort
    );
  };

  const filterFunc = () => {
    let api = "?";
    if (searchValue !== "") {
      api += `&search=${searchValue}`;
    }
    if (isActiveFilter !== "") {
      api += `&is_active=${isActiveFilter}`;
    }
    // Reset pagination when filtering
    activePag.current = 0;
    dispatch(getCurrenciesFunc(api));
  };

  const sort = 20;
  const activePag = useRef(0);

  const onClick = (i) => {
    activePag.current = i;
  };

  const handleDeleteItem = (id, entityName = "") => {
    handleDeleteEntity({
      id,
      entityName: entityName,
      deleteFunc: deleteCurrencyFunc,
      dispatch,
      refetchFunc: () => {
        // Reset pagination and refresh data
        activePag.current = 0;
        dispatch(getCurrenciesFunc());
      },
      t,
    });
  };

  const handleSetDefault = async (id) => {
    try {
      const result = await dispatch(setDefaultCurrencyFunc(id));
      if (setDefaultCurrencyFunc.fulfilled.match(result)) {
        toast.success(t("Currency set as default successfully"));
        dispatch(getCurrenciesFunc());
      } else {
        toast.error(result.payload?.message || t("Failed to set default currency"));
      }
    } catch (error) {
      toast.error(t("An error occurred"));
    }
  };

  return (
    <>
      <PageTitle
        motherMenu={"Dashboard"}
        activeMenu={"Currencies"}
        customClasses={"my-0"}
      />

      <div className="col-12">
        <div className="card">
          <div className="card-header row">
            <div className="col-6">
              <SearchBox
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onSearch={() => filterFunc()}
              />
            </div>
            <div className="col-6 text-end">
              <select
                className="form-select me-2"
                value={isActiveFilter}
                onChange={(e) => {
                  setIsActiveFilter(e.target.value);
                  setTimeout(() => filterFunc(), 100);
                }}
                style={{ width: "auto", display: "inline-block" }}
              >
                <option value="">{t("All Status")}</option>
                <option value="true">{t("Active")}</option>
                <option value="false">{t("Inactive")}</option>
              </select>
              <button
                className="btn btn-primary btn-md"
                onClick={() => setShowNewCurrencyForm(true)}
              >
                {t("Create")}
              </button>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive w-100">
              <div id="example_wrapper" className="dataTables_wrapper">
                <Table
                  showIndex={true}
                  startIndex={activePag.current * sort}
                  columns={[
                    { header: t("Name"), render: (row) => row.name },
                    { header: t("Code"), render: (row) => row.code },
                    { header: t("Symbol"), render: (row) => row.symbol },
                    {
                      header: t("Position"),
                      render: (row) => (
                        <span className={`badge ${row.position === 'before' ? 'bg-info' : 'bg-secondary'}`}>
                          {t(row.position)}
                        </span>
                      ),
                    },
                    {
                      header: t("Status"),
                      render: (row) => (
                        <span
                          className={`badge ${
                            row.is_active ? "bg-success" : "bg-danger"
                          }`}
                        >
                          {row.is_active ? t("Active") : t("Inactive")}
                        </span>
                      ),
                    },
                    {
                      header: t("Default"),
                      render: (row) => (
                        <span
                          className={`badge ${
                            row.is_default ? "bg-warning" : "bg-light text-dark"
                          }`}
                        >
                          {row.is_default ? t("Yes") : t("No")}
                        </span>
                      ),
                    },
                    {
                      header: t("Sort Order"),
                      render: (row) => row.sort_order || 0,
                    },
                  ]}
                  data={getCurrentPageData()}
                  loading={getCurrenciesLoading}
                  actions={(currency) => (
                    <>
                      <button
                        className="btn btn-primary shadow btn-xs sharp me-1"
                        onClick={() => {
                          setShowUpdateForm(true);
                          setCurrencyToUpdate(currency);
                        }}
                      >
                        <i className="fas fa-pencil-alt"></i>
                      </button>
                      {!currency.is_default && (
                        <button
                          className="btn btn-warning shadow btn-xs sharp me-1"
                          onClick={() => handleSetDefault(currency.id)}
                          disabled={setDefaultLoading}
                          title={t("Set as Default")}
                        >
                          <i className="fas fa-star"></i>
                        </button>
                      )}
                      {!currency.is_default && (
                        <button
                          className="btn btn-danger shadow btn-xs sharp"
                          onClick={() => handleDeleteItem(currency.id, currency.name)}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      )}
                    </>
                  )}
                />

                <Pagination
                  totalItems={currenciesData?.length || 0}
                  currentPage={activePag.current}
                  perPage={sort}
                  onPageChange={(page) => onClick(page)}
                  t={t}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <LoadingScreen loading={deleteCurrencyLoading || setDefaultLoading} />
      
      <NewCurrencyForm
        show={showNewCurrencyForm}
        onHide={() => {
          setShowNewCurrencyForm(false);
          dispatch(getCurrenciesFunc());
        }}
      />
      
      <UpdateCurrencyForm
        show={showUpdateForm}
        onHide={() => {
          setShowUpdateForm(false);
          dispatch(getCurrenciesFunc());
        }}
        currencyId={currencyToUpdate.id}
        oldName={currencyToUpdate.name}
        oldCode={currencyToUpdate.code}
        oldSymbol={currencyToUpdate.symbol}
        oldPosition={currencyToUpdate.position}
        oldStatus={currencyToUpdate.is_active}
        oldSortOrder={currencyToUpdate.sort_order}
      />
    </>
  );
}

export default CurrenciesPage;
